// src/pages/RMRDC/RmrdcDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  MenuItem,
  Snackbar,
  Alert,
  Slide,
  useMediaQuery,
  useTheme,
  alpha,
  Divider,
  Tooltip,
  LinearProgress,
  Fade,
  Zoom,
  Badge,
  Stack,
  Switch,
  FormControlLabel,
  Tab,
  Tabs,
  Rating,
  Skeleton,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

// ============================================
// ICON IMPORTS
// ============================================
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  ShoppingBag as ShoppingBagIcon,
  Inventory as InventoryIcon,
  AttachMoney as AttachMoneyIcon,
  Storefront as StorefrontIcon,
  Verified as VerifiedIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  LocalShipping as LocalShippingIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Star as StarIcon,
  Category as CategoryIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  LockOutlined as LockOutlinedIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  CalendarToday as CalendarTodayIcon,
  Map as MapIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Timeline as TimelineIcon,
  ShowChart as ShowChartIcon,
  Public as PublicIcon,
  GridOn as GridOnIcon,
  DataUsage as DataUsageIcon,
  CompareArrows as CompareArrowsIcon,
  Speed as SpeedIcon,
  AccessTime as AccessTimeIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';

// ============================================
// CHART IMPORTS
// ============================================
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  RadialBarChart,
  RadialBar,
  ScatterChart,
  Scatter,
  Treemap,
  Sankey,
} from 'recharts';

// Import shared components
import Preloader from '../../theme/Preloader';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import { formatCurrency, formatDate } from '../buyers/components/utils';

// Import Advanced Analytics Component
import AdvancedAnalytics from './AdvancedAnalytics';

// ============================================
// ENHANCED COLOR SCHEME
// ============================================
const COLORS = {
  primary: '#1A237E',
  primaryLight: '#283593',
  primaryDark: '#0D1445',
  secondary: '#00BCD4',
  accent: '#FF6F00',
  success: '#2E7D32',
  warning: '#E65100',
  error: '#C62828',
  info: '#01579B',
  gold: '#D4AF37',
  purple: '#6A1B9A',
  teal: '#00838F',
  pink: '#C2185B',
  indigo: '#3F51B5',
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

const CHART_COLORS = ['#1A237E', '#283593', '#D4AF37', '#2E7D32', '#C62828', '#FF6F00', '#01579B', '#6A1B9A', '#00838F', '#C2185B', '#3F51B5', '#00BCD4', '#FF5722', '#795548', '#607D8B'];

const STATUS_COLORS = {
  pending: '#FF9800',
  processing: '#2196F3',
  shipped: '#9C27B0',
  delivered: '#4CAF50',
  cancelled: '#F44336',
};

// ============================================
// API CONFIGURATION
// ============================================
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

const API_ENDPOINTS = {
  RMRDC: {
    DASHBOARD: `${API_BASE_URL}/rmrdc/dashboard.php`,
    EXPORT: `${API_BASE_URL}/rmrdc/export_csv.php`,
    USERS: `${API_BASE_URL}/rmrdc/users.php`,
    PRODUCTS: `${API_BASE_URL}/rmrdc/products.php`,
    ORDERS: `${API_BASE_URL}/rmrdc/orders.php`,
  },
};

// ============================================
// LOGGER
// ============================================
const logger = {
  info: (message, data) => console.log(`[RMRDC-DASH] ✅ ${message}`, data || ''),
  error: (message, error) => console.error(`[RMRDC-DASH] ❌ ${message}`, error || ''),
  debug: (message, data) => console.debug(`[RMRDC-DASH] 🔍 ${message}`, data || ''),
  warn: (message, data) => console.warn(`[RMRDC-DASH] ⚠️ ${message}`, data || ''),
};

// ============================================
// HELPER FUNCTIONS
// ============================================
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const base = IMAGE_BASE_URL.endsWith('/') ? IMAGE_BASE_URL : IMAGE_BASE_URL + '/';
  return `${base}${imagePath}`;
};

const formatNumber = (num) => {
  if (num === undefined || num === null) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
};

const getStatusColor = (status) => {
  const colors = {
    active: COLORS.success,
    inactive: COLORS.error,
    pending: COLORS.warning,
    delivered: COLORS.success,
    cancelled: COLORS.error,
    processing: COLORS.info,
    shipped: COLORS.purple,
    paid: COLORS.success,
    unpaid: COLORS.warning,
    verified: COLORS.success,
    unverified: COLORS.error,
  };
  return colors[status?.toLowerCase()] || COLORS.grey[500];
};

// ============================================
// CUSTOM COMPONENTS
// ============================================
const StatCard = ({ icon, title, value, subtitle, color, trend, trendValue, progress, onClick, extra }) => (
  <Zoom in style={{ transitionDelay: '100ms' }}>
    <Card
      onClick={onClick}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        cursor: onClick ? 'pointer' : 'default',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}30`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 40px ${color}25`,
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: COLORS.grey[600], fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.6rem' }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, color: color, fontSize: { xs: '1.2rem', sm: '1.6rem' } }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: COLORS.grey[600], display: 'block', mt: 0.5, fontWeight: 500 }}>
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                {trend === 'up' ? (
                  <TrendingUpIcon sx={{ fontSize: 14, color: COLORS.success }} />
                ) : trend === 'down' ? (
                  <TrendingDownIcon sx={{ fontSize: 14, color: COLORS.error }} />
                ) : null}
                <Typography variant="caption" sx={{ color: trend === 'up' ? COLORS.success : trend === 'down' ? COLORS.error : COLORS.grey[600], fontWeight: 700 }}>
                  {trendValue}
                </Typography>
              </Box>
            )}
            {extra && (
              <Typography variant="caption" sx={{ color: COLORS.grey[500], display: 'block', mt: 0.5, fontSize: '0.6rem' }}>
                {extra}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.15), color: color, width: 44, height: 44, border: `2px solid ${alpha(color, 0.15)}` }}>
            {icon}
          </Avatar>
        </Box>
        {progress !== undefined && progress !== null && (
          <LinearProgress
            variant="determinate"
            value={Math.min(progress, 100)}
            sx={{ mt: 2, height: 4, borderRadius: 3, bgcolor: alpha(color, 0.1), '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 } }}
          />
        )}
      </CardContent>
    </Card>
  </Zoom>
);

const InsightCard = ({ title, value, change, icon, color, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      borderRadius: 3,
      border: `1px solid ${COLORS.grey[200]}`,
      transition: 'all 0.2s ease',
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': {
        transform: onClick ? 'translateY(-2px)' : 'none',
        boxShadow: onClick ? 4 : 0,
      },
    }}
  >
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: alpha(color || COLORS.primary, 0.1), color: color || COLORS.primary, width: 40, height: 40 }}>
          {icon}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="textSecondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.6rem' }}>
            {title}
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ fontSize: '1rem' }}>
            {value}
          </Typography>
        </Box>
        {change !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {change >= 0 ? (
              <TrendingUpIcon sx={{ fontSize: 16, color: COLORS.success }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 16, color: COLORS.error }} />
            )}
            <Typography variant="caption" sx={{ color: change >= 0 ? COLORS.success : COLORS.error, fontWeight: 700 }}>
              {Math.abs(change)}%
            </Typography>
          </Box>
        )}
      </Box>
    </CardContent>
  </Card>
);

const GeographicTable = ({ data, title, columns, onRowClick }) => {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="textSecondary">No data available</Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((col, idx) => (
              <TableCell key={idx} sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: COLORS.grey[600] }}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, idx) => (
            <TableRow
              key={idx}
              onClick={() => onRowClick?.(row)}
              sx={{
                '&:hover': { bgcolor: alpha(COLORS.primary, 0.02), cursor: 'pointer' },
                '&:last-child td': { border: 0 },
              }}
            >
              {columns.map((col, colIdx) => (
                <TableCell key={colIdx} sx={{ fontSize: '0.75rem', py: 0.5 }}>
                  {col.render ? col.render(row[col.key]) : row[col.key] || '-'}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const RmrdcDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // ============================================
  // LOCAL STORAGE DATA
  // ============================================
  const userData = useMemo(() => {
    try {
      const data = localStorage.getItem('user_data');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const userType = localStorage.getItem('user_type') || 'rmrdc';

  // ============================================
  // STATE
  // ============================================
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [selectedTab, setSelectedTab] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [timeframe, setTimeframe] = useState('month');
  const [viewMode, setViewMode] = useState('grid');

  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allOrders, setAllOrders] = useState([]);

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalBuyers: 0,
    totalSuppliers: 0,
    totalAdmins: 0,
    activeUsers: 0,
    verifiedUsers: 0,
    totalProducts: 0,
    activeProducts: 0,
    pendingProducts: 0,
    outOfStock: 0,
    totalStock: 0,
    avgPrice: 0,
    avgRating: 0,
    totalOrders: 0,
    pendingOrders: 0,
    processingOrders: 0,
    shippedOrders: 0,
    deliveredOrders: 0,
    cancelledOrders: 0,
    totalRevenue: 0,
    avgOrderValue: 0,
    verifiedSuppliers: 0,
    unverifiedSuppliers: 0,
  });

  // Chart data states
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [categoryDistribution, setCategoryDistribution] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [userTypeDistribution, setUserTypeDistribution] = useState([]);

  // Geographic data states
  const [usersByState, setUsersByState] = useState([]);
  const [usersByLga, setUsersByLga] = useState([]);
  const [productsByState, setProductsByState] = useState([]);
  const [productsByLga, setProductsByLga] = useState([]);
  const [ordersByState, setOrdersByState] = useState([]);
  const [categoryByState, setCategoryByState] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [topProductsByState, setTopProductsByState] = useState([]);
  const [topSuppliers, setTopSuppliers] = useState([]);
  const [topBuyers, setTopBuyers] = useState([]);
  const [categoryInsights, setCategoryInsights] = useState([]);
  const [hourlyPatterns, setHourlyPatterns] = useState([]);
  const [weeklyTrends, setWeeklyTrends] = useState([]);
  const [insights, setInsights] = useState({});

  // Dialog states
  const [userDetailsDialog, setUserDetailsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [orderDetailsDialog, setOrderDetailsDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [productDetailsDialog, setProductDetailsDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [geographicDialog, setGeographicDialog] = useState(false);
  const [geographicData, setGeographicData] = useState(null);

  // ============================================
  // API FUNCTIONS
  // ============================================
  const fetchDashboardData = useCallback(async () => {
    logger.info('Fetching RMRDC dashboard data...');
    setLoading(true);

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found. Please login again.');
      }

      const url = `${API_ENDPOINTS.RMRDC.DASHBOARD}?user_id=${userId}&timeframe=${timeframe}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success && result.data) {
        const data = result.data;
        setDashboardData(data);
        
        // ============================================
        // CRITICAL FIX: Store the analytics data properly
        // ============================================
        
        // Create a clean analytics data object that persists
        const analyticsDataObject = {
          geography: {
            users_by_state: data.geography?.users_by_state || [],
            users_by_lga: data.geography?.users_by_lga || [],
            products_by_state: data.geography?.products_by_state || [],
            products_by_lga: data.geography?.products_by_lga || [],
            orders_by_state: data.geography?.orders_by_state || [],
            category_by_state: data.geography?.category_by_state || [],
            heatmap: data.geography?.heatmap || [],
          },
          categories: {
            distribution: data.categories?.distribution || [],
            detailed_insights: data.categories?.detailed_insights || [],
          },
          top_performers: {
            top_products_by_state: data.top_performers?.top_products_by_state || [],
            top_suppliers: data.top_performers?.top_suppliers || [],
            top_buyers: data.top_performers?.top_buyers || [],
          },
          trends: {
            monthly_revenue: data.trends?.monthly_revenue || [],
            product_performance: data.trends?.product_performance || [],
            hourly_patterns: data.trends?.hourly_patterns || [],
            weekly_trends: data.trends?.weekly_trends || [],
            order_status: data.trends?.order_status || [],
          },
          stats: data.stats || {},
          insights: data.insights || {},
          recent: data.recent || {},
        };
        
        // Store the analytics data
        setAnalyticsData(analyticsDataObject);
        
        // Update stats
        if (data.stats) {
          const userStats = data.stats.users || {};
          const productStats = data.stats.products || {};
          const orderStats = data.stats.orders || {};
          
          setStats({
            totalUsers: userStats.total_users || 0,
            totalBuyers: userStats.total_buyers || 0,
            totalSuppliers: userStats.total_suppliers || 0,
            totalAdmins: userStats.total_admins || 0,
            activeUsers: userStats.active_users || 0,
            verifiedUsers: userStats.verified_users || 0,
            totalProducts: productStats.total_products || 0,
            activeProducts: productStats.active_products || 0,
            pendingProducts: productStats.pending_products || 0,
            outOfStock: productStats.out_of_stock || 0,
            totalStock: productStats.total_stock || 0,
            avgPrice: productStats.avg_price || 0,
            avgRating: productStats.avg_rating || 0,
            totalOrders: orderStats.total_orders || 0,
            pendingOrders: orderStats.pending_orders || 0,
            processingOrders: orderStats.processing_orders || 0,
            shippedOrders: orderStats.shipped_orders || 0,
            deliveredOrders: orderStats.delivered_orders || 0,
            cancelledOrders: orderStats.cancelled_orders || 0,
            totalRevenue: orderStats.total_revenue || 0,
            avgOrderValue: orderStats.avg_order_value || 0,
            verifiedSuppliers: data.geography?.users_by_state?.reduce((acc, s) => acc + (s.verified || 0), 0) || 0,
            unverifiedSuppliers: data.geography?.users_by_state?.reduce((acc, s) => acc + (s.total - (s.verified || 0)), 0) || 0,
          });
        }
        
        // Update chart data
        if (data.trends) {
          setMonthlyRevenue(data.trends.monthly_revenue || []);
          setOrderStatusData(data.trends.order_status || []);
          setHourlyPatterns(data.trends.hourly_patterns || []);
          setWeeklyTrends(data.trends.weekly_trends || []);
        }
        
        if (data.categories) {
          setCategoryDistribution(data.categories.distribution || []);
          setCategoryInsights(data.categories.detailed_insights || []);
        }
        
        if (data.geography) {
          setUsersByState(data.geography.users_by_state || []);
          setUsersByLga(data.geography.users_by_lga || []);
          setProductsByState(data.geography.products_by_state || []);
          setProductsByLga(data.geography.products_by_lga || []);
          setOrdersByState(data.geography.orders_by_state || []);
          setCategoryByState(data.geography.category_by_state || []);
          setHeatmapData(data.geography.heatmap || []);
        }
        
        if (data.top_performers) {
          setTopProductsByState(data.top_performers.top_products_by_state || []);
          setTopSuppliers(data.top_performers.top_suppliers || []);
          setTopBuyers(data.top_performers.top_buyers || []);
        }
        
        if (data.recent) {
          setRecentUsers(data.recent.users || []);
          setRecentOrders(data.recent.orders || []);
        }
        
        if (data.insights) {
          setInsights(data.insights);
        }

        // ============================================
        // SET ANALYTICS DATA FROM API RESPONSE
        // ============================================
        if (data.analytics) {
          setAllProducts(data.analytics.products || []);
          setAllUsers(data.analytics.users || []);
          setAllOrders(data.analytics.orders || []);
          setAvailableStates(data.analytics.states || []);
          setAvailableCategories(data.analytics.categories?.map(c => c.name) || []);
        } else {
          // Fallback: extract data from existing response
          setAllProducts(
            data.top_performers?.top_products_by_state?.map(p => ({
              id: p.product_id,
              name: p.product_name,
              productType: p.category,
              supplier_name: p.supplier_name,
              state: p.state,
              average_rating: p.average_rating,
              price: p.price,
              quantity_in_stock: 0,
              discounts: 0,
              order_count: p.order_count,
              total_revenue: p.total_revenue,
              total_sold: p.total_sold,
            })) || []
          );
          
          setAllUsers(data.recent?.users || []);
          setAllOrders(data.recent?.orders || []);
          
          const statesFromGeo = data.geography?.users_by_state?.map(s => ({
            id: s.state_id || s.id,
            name: s.state,
            code: s.state_code
          })) || [];
          setAvailableStates(statesFromGeo);
          
          const catsFromData = data.categories?.distribution?.map(c => c.category) || [];
          setAvailableCategories(catsFromData);
        }
        
        logger.info('✅ RMRDC dashboard data loaded successfully');
      } else {
        throw new Error(result.message || 'Failed to fetch dashboard data');
      }
    } catch (error) {
      logger.error('Error fetching dashboard data:', error);
      setLoadingError(error.message);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load dashboard data. Please refresh.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [timeframe]);

  const loadData = useCallback(async () => {
    await fetchDashboardData();
  }, [fetchDashboardData]);

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token || (userType !== 'rmrdc' && userType !== 'super_admin')) {
      navigate('/RawAuth');
      return;
    }
    loadData();
  }, [loadData, navigate, userType]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleTabChange = useCallback((tab) => {
    const routes = {
      'dashboard': '/rmrdc/Dashboard',
      'analytics': '/rmrdc/analytics',
      'users': '/rmrdc/users',
      'products': '/rmrdc/products',
      'orders': '/rmrdc/orders',
      'settings': '/rmrdc/settings',
      'users-list': '/rmrdc/users',
      'users-suppliers': '/rmrdc/Dashboard/suppliers',
      'users-buyers': '/rmrdc/users/buyers',
      'users-roles': '/rmrdc/users/roles',
      'products-all': '/rmrdc/products',
      'products-categories': '/rmrdc/products/categories',
      'products-reviews': '/rmrdc/products/reviews',
      'orders-all': '/rmrdc/orders',
      'orders-pending': '/rmrdc/orders/pending',
      'orders-shipped': '/rmrdc/orders/shipped',
      'orders-delivered': '/rmrdc/orders/delivered',
      'orders-cancelled': '/rmrdc/orders/cancelled',
      'settings-general': '/rmrdc/settings',
      'settings-security': '/rmrdc/settings/security',
      'settings-backup': '/rmrdc/settings/backup',
      'settings-support': '/rmrdc/settings/support',
    };
    
    if (routes[tab]) {
      setSelectedTab(tab);
      navigate(routes[tab]);
    } else {
      setSelectedTab(tab);
      navigate(tab);
    }
  }, [navigate]);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    navigate('/RawAuth');
    setSnackbar({ open: true, message: 'Logged out successfully', severity: 'info' });
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    loadData();
    setLastRefreshed(new Date());
    setSnackbar({ open: true, message: 'Data refreshed', severity: 'info' });
  }, [loadData]);

  const handleTimeframeChange = useCallback((event, newTimeframe) => {
    if (newTimeframe !== null) {
      setTimeframe(newTimeframe);
    }
  }, []);

  // ============================================
  // EXPORT HANDLER
  // ============================================
  const handleExportReport = useCallback(async () => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        setSnackbar({ open: true, message: 'Please login again', severity: 'error' });
        return;
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      setSnackbar({ open: true, message: 'Generating report...', severity: 'info' });

      // Use the CSV export
      const link = document.createElement('a');
      link.href = `${API_ENDPOINTS.RMRDC.EXPORT}?user_id=${userId}&type=full`;
      link.download = `RMRDC_Report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({ open: true, message: 'Report downloaded successfully!', severity: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      setSnackbar({ open: true, message: 'Failed to export report. Please try again.', severity: 'error' });
    }
  }, []);

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  const renderStats = () => {
    const statItems = [
      { 
        icon: <PeopleIcon />, 
        title: 'Total Users', 
        value: stats.totalUsers, 
        subtitle: `${stats.activeUsers} active`, 
        color: COLORS.primary,
        trend: 'up',
        trendValue: `${insights.user_growth || 0}%`,
        onClick: () => handleTabChange('/rmrdc/users')
      },
      { 
        icon: <StorefrontIcon />, 
        title: 'Suppliers', 
        value: stats.totalSuppliers, 
        subtitle: `${stats.verifiedSuppliers} verified`, 
        color: COLORS.info,
        trend: 'up',
        trendValue: `${insights.supplier_verification_rate || 0}% verified`,
        onClick: () => handleTabChange('/rmrdc/Dashboard/suppliers')
      },
      { 
        icon: <ShoppingBagIcon />, 
        title: 'Orders', 
        value: stats.totalOrders, 
        subtitle: `${stats.pendingOrders} pending`, 
        color: COLORS.accent,
        trend: 'up',
        trendValue: `${insights.order_growth || 0}%`,
        onClick: () => handleTabChange('/rmrdc/orders')
      },
      { 
        icon: <InventoryIcon />, 
        title: 'Products', 
        value: stats.totalProducts, 
        subtitle: `${stats.activeProducts} active`, 
        color: COLORS.purple,
        trend: 'up',
        trendValue: `${stats.totalStock} in stock`,
        onClick: () => handleTabChange('/rmrdc/products')
      },
      { 
        icon: <VerifiedIcon />, 
        title: 'Verification', 
        value: `${insights.verification_rate || 0}%`, 
        subtitle: `${stats.verifiedUsers} verified users`, 
        color: COLORS.gold,
        progress: insights.verification_rate || 0,
      },
      { 
        icon: <AnalyticsIcon />, 
        title: 'Analytics', 
        value: 'Advanced', 
        subtitle: 'Deep insights', 
        color: COLORS.secondary,
        onClick: () => handleTabChange('analytics')
      },
    ];

    return (
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {statItems.map((item, index) => (
          <Grid size={{ xs: 6, sm: 4, md: 2 }} key={index}>
            <StatCard {...item} />
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderInsights = () => {
    const insightItems = [
      { title: 'Active User Rate', value: `${insights.active_user_rate || 0}%`, icon: <PeopleIcon fontSize="small" />, color: COLORS.primary },
      { title: 'Delivery Success', value: `${insights.delivery_success_rate || 0}%`, icon: <LocalShippingIcon fontSize="small" />, color: COLORS.success },
      { title: 'Payment Success', value: `${insights.payment_success_rate || 0}%`, icon: <AttachMoneyIcon fontSize="small" />, color: COLORS.info },
      { title: 'New Users (30d)', value: insights.new_users_30d || 0, icon: <PersonAddIcon fontSize="small" />, color: COLORS.purple },
      { title: 'Avg Processing', value: `${Math.round(insights.avg_processing_time || 0)} days`, icon: <AccessTimeIcon fontSize="small" />, color: COLORS.warning },
    ];

    return (
      <Grid container spacing={1.5} sx={{ mb: 4 }}>
        {insightItems.map((item, index) => (
          <Grid size={{ xs: 4, sm: 2 }} key={index}>
            <InsightCard {...item} />
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderGeographicCharts = () => {
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Users by State */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ borderRadius: 4, border: `1px solid ${COLORS.grey[200]}` }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PublicIcon sx={{ color: COLORS.primary }} />
                  Users by State
                </Typography>
                <Button size="small" onClick={() => handleTabChange('analytics')}>
                  Analyze
                </Button>
              </Box>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={usersByState.slice(0, 15)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grey[200]} horizontal={false} />
                    <XAxis type="number" axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="state" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} width={80} />
                    <RechartsTooltip 
                      contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
                      formatter={(value, name) => {
                        if (name === 'buyers') return [value, 'Buyers'];
                        if (name === 'suppliers') return [value, 'Suppliers'];
                        return [value, name];
                      }}
                    />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.7rem' }} />
                    <Bar dataKey="buyers" stackId="a" fill={COLORS.secondary} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="suppliers" stackId="a" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
                    <Bar dataKey="admins" stackId="a" fill={COLORS.gold} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Products by State */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ borderRadius: 4, border: `1px solid ${COLORS.grey[200]}` }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InventoryIcon sx={{ color: COLORS.success }} />
                  Products by State
                </Typography>
                <Button size="small" onClick={() => handleTabChange('analytics')}>
                  Analyze
                </Button>
              </Box>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={productsByState.slice(0, 15)}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grey[200]} vertical={false} />
                    <XAxis dataKey="state" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
                    <YAxis axisLine={false} tickLine={false} />
                    <RechartsTooltip 
                      contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
                      formatter={(value, name) => {
                        if (name === 'total_products') return [value, 'Products'];
                        if (name === 'total_stock') return [value, 'Stock'];
                        if (name === 'categories') return [value, 'Categories'];
                        return [value, name];
                      }}
                    />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.7rem' }} />
                    <Bar dataKey="total_products" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="categories" fill={COLORS.gold} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Orders by State */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ borderRadius: 4, border: `1px solid ${COLORS.grey[200]}` }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptIcon sx={{ color: COLORS.accent }} />
                  Orders by State
                </Typography>
                <Button size="small" onClick={() => handleTabChange('analytics')}>
                  Analyze
                </Button>
              </Box>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ordersByState.slice(0, 15)}>
                    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grey[200]} vertical={false} />
                    <XAxis dataKey="state" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
                    <YAxis yAxisId="left" axisLine={false} tickLine={false} />
                    <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} />
                    <RechartsTooltip 
                      contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
                      formatter={(value, name) => {
                        if (name === 'total_orders') return [value, 'Orders'];
                        if (name === 'total_revenue') return [`₦${value.toLocaleString()}`, 'Revenue'];
                        if (name === 'delivery_rate') return [`${value}%`, 'Delivery Rate'];
                        return [value, name];
                      }}
                    />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.7rem' }} />
                    <Bar yAxisId="left" dataKey="total_orders" fill={COLORS.accent} radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="delivery_rate" stroke={COLORS.success} strokeWidth={3} dot={{ fill: COLORS.success, r: 4 }} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Category by State */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card sx={{ borderRadius: 4, border: `1px solid ${COLORS.grey[200]}` }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CategoryIcon sx={{ color: COLORS.purple }} />
                  Category Distribution by State
                </Typography>
                <Button size="small" onClick={() => handleTabChange('analytics')}>
                  Analyze
                </Button>
              </Box>
              <Box sx={{ height: 300, overflow: 'auto' }}>
                {categoryByState.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="textSecondary">No category data available</Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>State</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>Category</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }} align="right">Count</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }} align="right">Stock</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }} align="right">Avg Price</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categoryByState.slice(0, 20).map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{item.state}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>
                              <Chip label={item.category || 'Uncategorized'} size="small" sx={{ height: 20, fontSize: '0.6rem' }} />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }} align="right">{item.count}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }} align="right">{item.total_stock}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }} align="right">{formatCurrency(item.avg_price)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderTopPerformers = () => {
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Top Suppliers */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 4, border: `1px solid ${COLORS.grey[200]}` }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StorefrontIcon sx={{ color: COLORS.info }} />
                  Top Suppliers
                </Typography>
                <Button size="small" onClick={() => handleTabChange('analytics')}>
                  Compare
                </Button>
              </Box>
              <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
                {topSuppliers.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="textSecondary">No supplier data available</Typography>
                  </Box>
                ) : (
                  topSuppliers.slice(0, 10).map((supplier, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        mb: 1,
                        borderRadius: 2,
                        border: `1px solid ${COLORS.grey[200]}`,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: alpha(COLORS.primary, 0.02),
                          borderColor: COLORS.primary,
                        },
                      }}
                    >
                      <Avatar sx={{ 
                        bgcolor: alpha(COLORS.primary, 0.08), 
                        color: COLORS.primary,
                        width: 40,
                        height: 40,
                      }}>
                        {supplier.company_name?.charAt(0).toUpperCase() || 'S'}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>{supplier.company_name || 'Unknown'}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            label={supplier.state || 'N/A'} 
                            size="small" 
                            sx={{ height: 18, fontSize: '0.5rem' }} 
                          />
                          <Chip 
                            label={`${supplier.product_count || 0} products`} 
                            size="small" 
                            sx={{ height: 18, fontSize: '0.5rem', bgcolor: alpha(COLORS.primary, 0.08) }} 
                          />
                          {supplier.business_verified && (
                            <Chip 
                              label="Verified" 
                              size="small" 
                              sx={{ height: 18, fontSize: '0.5rem', bgcolor: alpha(COLORS.success, 0.1), color: COLORS.success }} 
                              icon={<VerifiedIcon sx={{ fontSize: 12 }} />}
                            />
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" fontWeight={700} color="primary">
                          {formatCurrency(supplier.total_revenue || 0)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.6rem' }}>
                          {supplier.total_sold || 0} units sold
                        </Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Buyers */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 4, border: `1px solid ${COLORS.grey[200]}` }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon sx={{ color: COLORS.success }} />
                  Top Buyers
                </Typography>
                <Button size="small" onClick={() => handleTabChange('analytics')}>
                  Compare
                </Button>
              </Box>
              <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
                {topBuyers.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="textSecondary">No buyer data available</Typography>
                  </Box>
                ) : (
                  topBuyers.slice(0, 10).map((buyer, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        mb: 1,
                        borderRadius: 2,
                        border: `1px solid ${COLORS.grey[200]}`,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: alpha(COLORS.primary, 0.02),
                          borderColor: COLORS.primary,
                        },
                      }}
                    >
                      <Avatar sx={{ 
                        bgcolor: alpha(COLORS.success, 0.08), 
                        color: COLORS.success,
                        width: 40,
                        height: 40,
                      }}>
                        {buyer.buyer_name?.charAt(0).toUpperCase() || 'B'}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>{buyer.buyer_name || 'Unknown'}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            label={buyer.organisation || 'N/A'} 
                            size="small" 
                            sx={{ height: 18, fontSize: '0.5rem' }} 
                          />
                          <Chip 
                            label={buyer.state || 'N/A'} 
                            size="small" 
                            sx={{ height: 18, fontSize: '0.5rem', bgcolor: alpha(COLORS.grey[400], 0.08) }} 
                          />
                          <Chip 
                            label={`${buyer.total_orders || 0} orders`} 
                            size="small" 
                            sx={{ height: 18, fontSize: '0.5rem', bgcolor: alpha(COLORS.accent, 0.08), color: COLORS.accent }} 
                          />
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" fontWeight={700} color="success">
                          {formatCurrency(buyer.total_spent || 0)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.6rem' }}>
                          {buyer.delivered_orders || 0} delivered
                        </Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderDashboard = () => (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              <AdminPanelSettingsIcon sx={{ color: COLORS.gold, fontSize: 32 }} />
              RMRDC Admin Dashboard
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Welcome back, {userData?.name || 'Administrator'} 👋
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <ToggleButtonGroup
              size="small"
              value={timeframe}
              exclusive
              onChange={handleTimeframeChange}
              sx={{ height: 36 }}
            >
              <ToggleButton value="week" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>Week</ToggleButton>
              <ToggleButton value="month" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>Month</ToggleButton>
              <ToggleButton value="year" sx={{ fontSize: '0.7rem', textTransform: 'none' }}>Year</ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              sx={{ borderRadius: 2, textTransform: 'none', height: 36 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AssessmentIcon />}
              onClick={handleExportReport}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                height: 36,
                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.gold})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${COLORS.primaryDark}, ${COLORS.primary})`,
                },
              }}
            >
              Export Report
            </Button>
            <Button
              variant="outlined"
              startIcon={<AnalyticsIcon />}
              onClick={() => handleTabChange('analytics')}
              sx={{ borderRadius: 2, textTransform: 'none', height: 36 }}
            >
              Advanced Analytics
            </Button>
          </Box>
        </Box>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
          Last updated: {lastRefreshed.toLocaleString()}
        </Typography>
      </Box>

      {renderStats()}
      {renderInsights()}
      
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2, mt: 4 }}>
        Geographic Intelligence
      </Typography>
      {renderGeographicCharts()}
      
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2, mt: 4 }}>
        Top Performers
      </Typography>
      {renderTopPerformers()}
      
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2, mt: 4 }}>
        Recent Activity
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 4, border: `1px solid ${COLORS.grey[200]}` }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Recent Orders</Typography>
                <Button size="small" onClick={() => handleTabChange('/rmrdc/orders')} sx={{ textTransform: 'none' }}>
                  View All
                </Button>
              </Box>
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                {recentOrders.slice(0, 5).map((order, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      mb: 1,
                      borderRadius: 2,
                      border: `1px solid ${COLORS.grey[200]}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: alpha(COLORS.primary, 0.02),
                      },
                    }}
                  >
                    <Avatar sx={{ bgcolor: alpha(STATUS_COLORS[order.status] || COLORS.grey[500], 0.1), color: STATUS_COLORS[order.status] || COLORS.grey[500] }}>
                      <ReceiptIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600}>Order #{order.order_reference || order.id}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="textSecondary">{order.buyer_name || 'Guest'}</Typography>
                        <Chip
                          label={order.status}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.5rem',
                            bgcolor: alpha(STATUS_COLORS[order.status] || COLORS.grey[500], 0.1),
                            color: STATUS_COLORS[order.status] || COLORS.grey[500],
                          }}
                        />
                      </Box>
                    </Box>
                    <Typography variant="caption" fontWeight={700} color="primary">
                      {formatCurrency(order.total_amount)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 4, border: `1px solid ${COLORS.grey[200]}` }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>Recent Users</Typography>
                <Button size="small" onClick={() => handleTabChange('/rmrdc/users')} sx={{ textTransform: 'none' }}>
                  View All
                </Button>
              </Box>
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                {recentUsers.slice(0, 5).map((user, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      mb: 1,
                      borderRadius: 2,
                      border: `1px solid ${COLORS.grey[200]}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: alpha(COLORS.primary, 0.02),
                      },
                    }}
                  >
                    <Avatar sx={{ bgcolor: alpha(COLORS.primary, 0.08), color: COLORS.primary }}>
                      {user.email?.charAt(0).toUpperCase() || 'U'}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{user.email}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={user.user_type}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.5rem',
                            bgcolor: alpha(
                              user.user_type === 'super_admin' ? COLORS.gold :
                              user.user_type === 'supplier' ? COLORS.info :
                              COLORS.success, 0.1
                            ),
                            color: user.user_type === 'super_admin' ? COLORS.gold :
                              user.user_type === 'supplier' ? COLORS.info :
                              COLORS.success,
                          }}
                        />
                        {user.is_verified && <VerifiedIcon sx={{ fontSize: 12, color: COLORS.success }} />}
                      </Box>
                    </Box>
                    <Typography variant="caption" color="textSecondary">{formatDate(user.created_at)}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  if (loading && !dashboardData) {
    return <Preloader text="Loading Dashboard..." size={60} fullScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: COLORS.grey[50] }}>
      <Sidebar
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        isMobile={isMobile}
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
        userData={userData}
        onLogout={handleLogout}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: 'calc(100% - 280px)' },
        }}
      >
        <Header
          isMobile={isMobile}
          onDrawerToggle={() => setDrawerOpen(true)}
          selectedTab={selectedTab}
          menuItems={[]}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          cartItemCount={0}
          onCartOpen={() => {}}
          notifications={[]}
          userData={userData}
          onLogout={handleLogout}
          onChangePassword={() => {}}
          onSettings={() => handleTabChange('/rmrdc/settings')}
        />

        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          {selectedTab === 'dashboard' && renderDashboard()}
          
          {selectedTab === 'analytics' && (
            <AdvancedAnalytics
              data={analyticsData}
              loading={loading}
              onRefresh={handleRefresh}
              usersData={allUsers}
              productsData={allProducts}
              ordersData={allOrders}
              states={availableStates}
              categories={availableCategories}
            />
          )}
          
          {selectedTab === 'users' || selectedTab === 'users-list' || selectedTab === 'users-suppliers' || selectedTab === 'users-buyers' || selectedTab === 'users-roles' ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h4" fontWeight={700}>User Management</Typography>
              <Typography color="textSecondary">Coming soon...</Typography>
            </Box>
          ) : null}
          
          {selectedTab === 'products' || selectedTab === 'products-all' || selectedTab === 'products-categories' || selectedTab === 'products-reviews' ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h4" fontWeight={700}>Product Management</Typography>
              <Typography color="textSecondary">Coming soon...</Typography>
            </Box>
          ) : null}
          
          {selectedTab === 'orders' || selectedTab === 'orders-all' || selectedTab === 'orders-pending' || selectedTab === 'orders-shipped' || selectedTab === 'orders-delivered' || selectedTab === 'orders-cancelled' ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h4" fontWeight={700}>Order Management</Typography>
              <Typography color="textSecondary">Coming soon...</Typography>
            </Box>
          ) : null}
          
          {selectedTab === 'settings' || selectedTab === 'settings-general' || selectedTab === 'settings-security' || selectedTab === 'settings-backup' || selectedTab === 'settings-support' ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h4" fontWeight={700}>Settings</Typography>
              <Typography color="textSecondary">Coming soon...</Typography>
            </Box>
          ) : null}
        </Box>
        <Footer />
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ borderRadius: 3, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <style>
        {`
          ::-webkit-scrollbar { width: 8px; height: 8px; }
          ::-webkit-scrollbar-track { background: ${COLORS.grey[100]}; border-radius: 4px; }
          ::-webkit-scrollbar-thumb { background: ${COLORS.grey[300]}; border-radius: 4px; }
          ::-webkit-scrollbar-thumb:hover { background: ${COLORS.grey[400]}; }
          @media print {
            .MuiAppBar-root, .MuiDrawer-root, .MuiButton-root { display: none !important; }
            .MuiCard-root { box-shadow: none !important; border: 1px solid #ddd !important; }
          }
        `}
      </style>
    </Box>
  );
};

export default RmrdcDashboard;